import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { AdminLayout } from "../../layouts";
import { Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControl, FormControlLabel, FormHelperText, Grid, IconButton, InputLabel, Menu, MenuItem, Paper, Select, Switch, TextField, Typography } from "@mui/material";
import { Delete, Edit, MoreVert } from "@mui/icons-material";
import { DataGrid, GridToolbar, GridRowsProp, GridColDef, GridFilterModel, GridColumnVisibilityModel, GridColumnGroupingModel, GridCellParams } from '@mui/x-data-grid';
import { confirmarPago, editFactura, editGrupo, editInscripcion, getColegiosAdmin, getCursosAdmin, getGruposAdmin, getInscripcionesByGroupId, getSedesCorreosCR, reenviarPago } from "../../api/providers";
import { LoadingScreen } from "../../ui/components/LoadingScreen";
import Cookies from "js-cookie";
import { useForm, watch as watchForm } from "react-hook-form"; // Import watch function
import { showAlert } from "../../helpers/alerts";

interface Inscripcion {
  id: number;
  estudianteId: number;
  grupoId: number;
  pagoRealizado: boolean;
  libroEntregado: boolean;
  estadoInscripcion: string;
  listaDeEspera: boolean;
  facturaRequerida: boolean;
  encargadoRetiroNombre: string;
  encargadoRetiroTelefono: string;
  sedeCorreosCRId: number;
}

const RowMenu = ({ rowIndex, data, handleEditInscripcion, handleEditGrupo, handleReenviarPago, handleConfirmarPago, handleEditFactura }: any) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        aria-label="more"
        id={`long-button-${rowIndex}`}
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVert />
      </IconButton>
      <Menu
        id={`long-menu-${rowIndex}`}
        MenuListProps={{
          'aria-labelledby': `long-button-${rowIndex}`,
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={() => { handleEditInscripcion(data); handleClose(); }}>Editar Inscripción</MenuItem>
        <MenuItem onClick={() => { handleEditGrupo(data); handleClose(); }}>Editar Grupo</MenuItem>
        <MenuItem onClick={() => { handleEditFactura(data); handleClose(); }}>Editar Factura</MenuItem>
        <MenuItem onClick={() => { handleReenviarPago(data); handleClose(); }}>Re-enviar correo de pago</MenuItem>
        <MenuItem onClick={() => { handleConfirmarPago(data); handleClose(); }}>Enviar correo pago confirmado</MenuItem>
      </Menu>
    </>
  );
};

export const StudentsByGroupOrCurso = () => {
  const token: string | undefined = Cookies.get("token");
  const location = useLocation();
  const { cursoId } = location.state || "";
  const { groupId } = location.state || "";
  const { id } = useParams();

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'actions', headerName: '', width: 50,
      renderCell: (params) => <RowMenu rowIndex={params.row.id} data={params.row} handleEditInscripcion={handleEditInscripcion} handleEditGrupo={handleEditGrupo} handleReenviarPago={handleReenviarPago} handleConfirmarPago={handleConfirmarPago} handleEditFactura={handleEditFactura} />,
    },
    { field: 'fechaInscripcion', headerName: 'Fecha Inscripcion', width: 130, type: 'date', headerClassName: 'datos-inscripcion', },
    { field: 'grupo', headerName: 'Grupo', width: 100, headerClassName: 'datos-inscripcion', },
    { field: 'estadoInscripcion', headerName: 'Estado', width: 120, headerClassName: 'datos-inscripcion', },
    {
      field: 'pagoRealizado', headerName: 'Pago Realizado', headerAlign: "left", width: 130, type: 'boolean', headerClassName: 'datos-inscripcion',
      renderCell: (params: GridCellParams) => (
        <Chip
          label={params.value === true ? "Realizado" : "Pendiente"}
          sx={{
            backgroundColor: params.value === true ? "#81C784" : "primary.light",
            color: "white",
            borderRadius: 1,
            width: "100%",
            fontWeight: "bold",
            fontSize: "1rem",
          }}
        />
      ),
    },
    {
      field: 'libroEntregado', headerName: 'Entrega de Libro', headerAlign: "left", width: 130, type: 'boolean', headerClassName: 'datos-inscripcion',
      renderCell: (params: GridCellParams) => (
        <Chip
          label={params.value === true ? "Entregado" : "Pendiente"}
          sx={{
            backgroundColor: params.value === true ? "#81C784" : "primary.light",
            color: "white",
            borderRadius: 1,
            width: "100%",
            fontWeight: "bold",
            fontSize: "1rem",
          }}
        />
      ),
    },
    {
      field: 'listaDeEspera', headerName: 'En Lista de Espera', width: 140, headerAlign: 'left', type: 'boolean', headerClassName: 'datos-inscripcion',
      renderCell: (params: GridCellParams) => (
        <Chip
          label={params.value === true ? "Sí" : "No"}
          sx={{
            backgroundColor: params.value === true ? "secondary.main" : "primary.light",
            color: "white",
            borderRadius: 1,
            width: "60%",
            fontWeight: "bold",
            fontSize: "1rem",
          }}
        />
      ),
    },
    {
      field: 'facturaRequerida', headerName: 'Requiere Factura', width: 130, type: 'boolean', headerAlign: 'left', headerClassName: 'datos-inscripcion',
      renderCell: (params: GridCellParams) => (
        <Chip
          label={params.value === true ? "Sí" : "No"}
          sx={{
            backgroundColor: params.value === true ? "#81C784" : "primary.light",
            color: "white",
            borderRadius: 1,
            width: "60%",
            fontWeight: "bold",
            fontSize: "1rem",
          }}
        />
      ),
    },
    {
      field: 'fechaPago', headerName: 'Fecha Pago', width: 100, type: 'date', headerClassName: 'datos-inscripcion',
      renderCell: (params: GridCellParams) => (
        <Chip
          label={params.value?.toLocaleDateString() ?? "-"}
          sx={{
            backgroundColor: params.value ? "" : "",
            color: "#292929",
            borderRadius: 1,
            width: "100%",
            fontWeight: "bold",
            fontSize: "1rem",
          }}
        />
      ),
    },
    { field: 'nombreEstudiante', headerName: 'Nombre', width: 150, headerClassName: 'datos-estudiante', },
    { field: 'correoEstudiante', headerName: 'Correo', width: 150, headerClassName: 'datos-estudiante', },
    {
      field: 'tipoCedula', headerName: 'Tipo de cedula', width: 130, headerClassName: 'datos-estudiante',
      renderCell: (params: GridCellParams) => (
        <Chip
          label={params.value as string}
          sx={{
            backgroundColor: params.value === "Fisica" ? "#64B5F6" :
              params.value === "Juridica" ? "#F06292" :
                params.value === "DIMEX" ? "#BA68C8 " :
                  params.value === "NITE" ? "#FFA726 " :
                    params.value === "Extranjero" ? "#4DB6AC" :
                      "grey",
            color: "white",
            borderRadius: 1,
            width: "100%",
            fontWeight: "bold",
            fontSize: "1rem",
          }}
        />
      ),
    },
    { field: 'cedulaEstudiante', headerName: 'Cedula', width: 120, headerClassName: 'datos-estudiante', },
    {
      field: 'telefonoEstudiante', headerName: 'Teléfono Estudiante', width: 175, type: 'string', headerClassName: 'datos-estudiante',
      renderCell: (params: GridCellParams) => {
        const telefono = params.value as string;
        const codigoPais = telefono.substring(0, 4);
        const restoNumero = telefono.substring(4);
        const numeroFormateado = `${restoNumero.slice(0, 4)}-${restoNumero.slice(4)}`;

        return (
          <Chip
            label={codigoPais + " " + numeroFormateado}
            sx={{
              width: "100%",
              backgroundColor: "#D3E4F5",
              fontWeight: "bold",
              fontSize: "0.9rem",
              color: "#233367",
              borderRadius: 1,
            }} />);
      },
    },
    {
      field: 'condicionEscolarEstudiante', headerName: 'Condicion Escolar', width: 150, headerAlign: 'center', align: 'center', headerClassName: 'datos-estudiante',
      renderCell: (params: GridCellParams) => (
        <Chip
          label={params.value as string}
          sx={{
            backgroundColor: params.value === "activo" ? "#81C784" :
              params.value === "egresado" ? "secondary.main" : "grey",
            color: "white",
            borderRadius: 1,
            width: "100%",
            fontWeight: "bold",
            fontSize: "1rem",
          }} />),
    },
    { field: 'colegio', headerName: 'Colegio', width: 150, headerClassName: 'datos-estudiante', },
    { field: 'provinciaColegio', headerName: 'Provincia', width: 120, headerClassName: 'datos-estudiante', },
    { field: 'nombreEncargado', headerName: 'Nombre del Encargado', width: 175, headerClassName: 'datos-estudiante', },
    { field: 'correoEncargado', headerName: 'Correo del encargado', width: 175, headerClassName: 'datos-estudiante', },
    {
      field: 'telefonoEncargado', headerName: 'Telefono del Encargado', width: 175, headerClassName: 'datos-estudiante',
      renderCell: (params: GridCellParams) => {
        const telefono = params.value as string ?? "";
        const codigoPais = telefono.substring(0, 4);
        const restoNumero = telefono.substring(4);
        const numeroFormateado = `${restoNumero.slice(0, 4)}-${restoNumero.slice(4)}`;

        return (
          <Chip
            label={codigoPais + " " + numeroFormateado}
            sx={{
              width: "100%",
              backgroundColor: "#D3E4F5",
              fontWeight: "bold",
              fontSize: "0.9rem",
              color: "#233367",
              borderRadius: 1,
            }} />);
      },
    },
    { field: 'nombreSede', headerName: 'Sede', width: 150, headerClassName: 'datos-retiro-documentos', },
    { field: 'direccionSede', headerName: 'Direccion', width: 150, headerClassName: 'datos-retiro-documentos', },
    { field: 'provinciaSede', headerName: 'Provincia', width: 120, headerClassName: 'datos-retiro-documentos', },
    { field: 'encargadoRetiroNombre', headerName: 'Nombre del Encargado de retiros', width: 185, headerClassName: 'datos-retiro-documentos', },
    {
      field: 'encargadoRetiroTelefono', headerName: 'Telefono del Encargado de retiros', width: 185, headerClassName: 'datos-retiro-documentos',
      renderCell: (params: GridCellParams) => {
        const telefono = params.value as string ?? "";
        const codigoPais = telefono.substring(0, 4);
        const restoNumero = telefono.substring(4);
        const numeroFormateado = `${restoNumero.slice(0, 4)}-${restoNumero.slice(4)}`;

        return (
          <Chip
            label={codigoPais + " " + numeroFormateado}
            sx={{
              width: "100%",
              backgroundColor: "#D3E4F5",
              fontWeight: "bold",
              fontSize: "0.9rem",
              color: "#233367",
              borderRadius: 1,
            }} />);
      },
    },
    {
      field: 'tipoCedulaFE', headerName: 'Tipo de cedula FE', width: 130, headerClassName: 'datos-factura-electronica',
      renderCell: (params: GridCellParams) => (
        <Chip
          label={params.value as string ?? "-"}
          sx={{
            backgroundColor: params.value === "Fisica" ? "#64B5F6" :
              params.value === "Juridica" ? "#F06292" :
                params.value === "DIMEX" ? "#BA68C8 " :
                  params.value === "NITE" ? "#FFA726 " :
                    params.value === "Extranjero" ? "#4DB6AC" :
                      "grey",
            color: "white",
            borderRadius: 1,
            width: "100%",
            fontWeight: "bold",
            fontSize: "1rem",
          }}
        />
      ),
    },
    { field: 'cedulaFE', headerName: 'Cedula para FE', width: 130, headerClassName: 'datos-factura-electronica', },
    { field: 'correoFE', headerName: 'Correo para FE', width: 150, headerClassName: 'datos-factura-electronica', },
    {
      field: 'telefonoFE', headerName: 'Telefono para FE', width: 175, headerClassName: 'datos-factura-electronica',
      renderCell: (params: GridCellParams) => {
        const telefonoFE = params.value as string ?? "";
        const codigoPais = telefonoFE.substring(0, 4);
        const restoNumero = telefonoFE.substring(3);
        const numeroFormateado = `${restoNumero.slice(0, 4)}-${restoNumero.slice(4)}`;

        return (
          <Chip
            label={codigoPais + " " + numeroFormateado}
            sx={{
              width: "100%",
              backgroundColor: "#D3E4F5",
              fontWeight: "bold",
              fontSize: "0.9rem",
              color: "#233367",
              borderRadius: 1,
            }} />);
      },
    },
  ], []);

  const [cursos, setCursos] = useState<any[]>([]);
  const [grupos, setGrupos] = useState<any[]>([]);
  const [estudiantes, setEstudiantes] = useState<Inscripcion[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCursoId, setSelectedCursoId] = useState<number | ''>('');
  const [selectedGroupId, setSelectedGroupId] = useState<number | '' | 'allGroups'>('');
  const [colegios, setColegios] = useState<any[]>([]);
  const [sedesCorreosCR, setSedesCorreosCR] = useState<any[]>([]);

  const refetchData = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      if (selectedGroupId === "allGroups") {
        // Fetch all groups for the selected curso
        if (!selectedCursoId) throw new Error("No se seleccionó un curso");

        const response = await getGruposAdmin(token, selectedCursoId);
        if (!response.ok) throw new Error("No se pudieron cargar los grupos");

        const gruposDelCurso = response.grupos.grupos;

        const allInscripciones = await Promise.all(
          gruposDelCurso.map(async (grupo: any) => {
            const resp = await getInscripcionesByGroupId(token, grupo.id);
            return resp.data.inscripciones.map(transformInscripcion);
          })
        );

        setEstudiantes(allInscripciones.flat());
      } else {
        // Fetch data for a specific group
        const response = await getInscripcionesByGroupId(token, +selectedGroupId);
        if (!response.ok) throw new Error("No se pudieron cargar las inscripciones");

        const inscripcionesTransformadas = response.data.inscripciones.map(transformInscripcion);
        setEstudiantes(inscripcionesTransformadas);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedGroupId, selectedCursoId, token]);
  
  useEffect(() => {
    if (cursoId) {
      setSelectedCursoId(cursoId);
    }
  }, [cursoId]);

  useEffect(() => {
    const fetchSedeCorreosCR = async () => {
      setLoading(true);
      const response = await getSedesCorreosCR();

      if (!response.ok) {
        setError('No se pudo cargar las sedes de correos de Costa Rica');
        setSedesCorreosCR([]);
      } else {
        setSedesCorreosCR(response.sedes.sedeCorreosCR);
      }
      setLoading(false);
    };

    fetchSedeCorreosCR();
  }, []);

  useEffect(() => {
    const fetchColegios = async () => {
      if (!token) return;
      const response = await getColegiosAdmin(token);
      if (!response.ok) {
        setError('No se pudo cargar los colegios');
        setColegios([]);
      } else {
        setColegios(response.colegios.colegios);
      }
    };

    fetchColegios();
  }, [token]);

  useEffect(() => {
    const fetchCursos = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const response = await getCursosAdmin(token);
        if (!response.ok) throw new Error("No se pudieron cargar los cursos");
        setCursos(response.cursos.cursos);
      } catch (error: any) {
        setError(error.message);
        setCursos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCursos();
  }, [token]);


  const handleCursoChange = (event: any) => {
    const cursoId = event.target.value;
    setSelectedCursoId(cursoId);
    setSelectedGroupId('');
    fetchGrupos(cursoId);
  };

  const fetchGrupos = async (cursoId: number | '') => {
    if (!token) return;
    if (!cursoId) {
      setGrupos([]);
      return;
    }

    setLoading(true);
    try {
      const response = await getGruposAdmin(token, cursoId);
      if (!response.ok) throw new Error("No se pudieron cargar los grupos");
      setGrupos(response.grupos.grupos);
    } catch (error: any) {
      setError(error.message);
      setGrupos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGrupoChange = async (event: any) => {
    const groupId = event.target.value;
    setSelectedGroupId(groupId);

    if (groupId === "allGroups") {
      if (!selectedCursoId) {
        setEstudiantes([]);
        return;
      }

      setLoading(true);
      try {
        const response = await getGruposAdmin(token!, selectedCursoId);
        if (!response.ok) throw new Error("No se pudieron cargar los grupos");
        const gruposDelCurso = response.grupos.grupos;

        const allInscripciones = await Promise.all(
          gruposDelCurso.map(async (grupo: any) => {
            const resp = await getInscripcionesByGroupId(token!, grupo.id);
            return resp.data.inscripciones.map(transformInscripcion);
          })
        );

        setEstudiantes(allInscripciones.flat());
      } catch (error: any) {
        setError(error.message);
        setEstudiantes([]);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(true);
      try {
        const response = await getInscripcionesByGroupId(token!, groupId);
        if (!response.ok) throw new Error("No se pudieron cargar las inscripciones");

        const inscripcionesTransformadas = response.data.inscripciones.map(transformInscripcion);
        setEstudiantes(inscripcionesTransformadas);
      } catch (error: any) {
        setError(error.message);
        setEstudiantes([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const transformInscripcion = (inscripcion: any): Inscripcion => ({
    ...inscripcion,
    fechaInscripcion: new Date(inscripcion.fechaInscripcion.split("Z")[0]),
    fechaPago: inscripcion.fechaPago ? new Date(inscripcion.fechaPago.split("Z")[0]) : null,
    grupo: inscripcion.grupo?.nombre,
    nombreEstudiante: `${inscripcion.estudiante?.nombre} ${inscripcion.estudiante?.primerApellido} ${inscripcion.estudiante?.segundoApellido}`,
    correoEstudiante: inscripcion.estudiante?.correo,
    tipoCedula: inscripcion.estudiante?.tipoCedula ?? "-",
    cedulaEstudiante: inscripcion.estudiante?.cedula,
    telefonoEstudiante: inscripcion.estudiante?.telefono,
    condicionEscolarEstudiante: inscripcion.estudiante?.condicionEscolar,
    colegio: inscripcion.estudiante.colegio?.nombre,
    provinciaColegio: inscripcion.estudiante.colegio?.provincia,
    nombreEncargado: inscripcion.estudiante?.nombreEncargado ?? "-",
    correoEncargado: inscripcion.estudiante?.correoEncargado ?? "-",
    telefonoEncargado: inscripcion.estudiante?.telefonoEncargado ?? "-",
    nombreSede: inscripcion.sedeCorreosCR?.nombre,
    direccionSede: inscripcion.sedeCorreosCR?.direccion,
    provinciaSede: inscripcion.sedeCorreosCR?.provincia,
    tipoCedulaFE: inscripcion.facturaElectronica?.tipoCedula ?? "-",
    cedulaFE: inscripcion.facturaElectronica?.cedula ?? "-",
    correoFE: inscripcion.facturaElectronica?.correo ?? "-",
    telefonoFE: inscripcion.facturaElectronica?.telefono ?? "-",
  });

  const rows: GridRowsProp = estudiantes;

  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: [],
    quickFilterExcludeHiddenColumns: true,
    quickFilterValues: [''],
  });

  const [columnVisibilityModel, setColumnVisibilityModel] =
    React.useState<GridColumnVisibilityModel>({
      tipoCedula: false,
      condicionEscolarEstudiante: false,
      nombreEncargado: false,
      correoEncargado: false,
      telefonoEncargado: false,
      tipoCedulaFE: false,
      cedulaFE: false,
      correoFE: false,
      telefonoFE: false,
    });

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    formState: { errors: errorsEdit },
    reset: resetEdit,
    setValue: setValueEdit,
    watch // Use watch function
  } = useForm<Inscripcion>();

  useEffect(() => {
  }, [errorsEdit])

  const [openEditInscripcionDialog, setOpenEditInscripcionDialog] = useState(false);
  const [openEditGrupoDialog, setOpenEditGrupoDialog] = useState(false);
  const [openEditFacturaDialog, setOpenEditFacturaDialog] = useState(false);
  const [editData, setEditData] = useState<Inscripcion | null>(null);

  const handleEditFactura = useCallback((selectedInscripcion: any) => {
    if (selectedInscripcion) {
      setEditData(selectedInscripcion);
      resetEdit({
        estudianteId: selectedInscripcion.estudianteId,
        facturaRequerida: selectedInscripcion.facturaRequerida,
        facturaElectronica: {
          tipoCedula: selectedInscripcion.facturaElectronica?.tipoCedula ?? "",
          cedula: selectedInscripcion.facturaElectronica?.cedula ?? "",
          correo: selectedInscripcion.facturaElectronica?.correo ?? "",
          telefono: selectedInscripcion.facturaElectronica?.telefono ?? "",
        },
      });
      setOpenEditFacturaDialog(true);
    }
  }, [estudiantes, resetEdit]);

  const handleCloseEditFacturaDialog = () => {
    setOpenEditFacturaDialog(false);
  };

  const handleSaveEditFactura = handleSubmitEdit(async (data) => {
    if (!editData) return;

    try {
      const response = await editFactura(token!, editData.id, data);
      if (!response.ok) {
        handleCloseEditFacturaDialog();
        showAlert('Error', response.errorMessage.error, 'error');
        throw new Error('Error al editar la factura');
      }
      handleCloseEditFacturaDialog();
      showAlert('¡Éxito!', 'Factura actualizada exitosamente', 'success');
      refetchData(); // Refetch data after saving
    } catch (error: any) {
      showAlert('Error', error.message, 'error');
    }
  });

  const handleEditInscripcion = useCallback((selectedInscripcion: any) => {
    if (selectedInscripcion) {
      setEditData(selectedInscripcion);
      resetEdit({
        estudianteId: selectedInscripcion.estudianteId,
        grupoId: selectedInscripcion.grupoId,
        pagoRealizado: selectedInscripcion.pagoRealizado,
        libroEntregado: selectedInscripcion.libroEntregado,
        estadoInscripcion: selectedInscripcion.estadoInscripcion,
        listaDeEspera: selectedInscripcion.listaDeEspera,
        encargadoRetiroNombre: selectedInscripcion.encargadoRetiroNombre,
        encargadoRetiroTelefono: selectedInscripcion.encargadoRetiroTelefono,
        sedeCorreosCRId: selectedInscripcion.sedeCorreosCRId,
      });
      setOpenEditInscripcionDialog(true);
    }
  }, [estudiantes, resetEdit]);

  const handleEditGrupo = useCallback((selectedInscripcion: any) => {
    if (selectedInscripcion) {
      setEditData(selectedInscripcion);
      resetEdit({
        grupoId: selectedInscripcion.grupoId,
      });
      setOpenEditGrupoDialog(true);
    }
  }, [estudiantes, resetEdit]);

  const handleCloseEditInscripcionDialog = () => {
    setOpenEditInscripcionDialog(false);
  };

  const handleCloseEditGrupoDialog = () => {
    setOpenEditGrupoDialog(false);
  };

  const handleSaveEditInscripcion = handleSubmitEdit(async (data) => {
    if (!editData) return;

    try {
      const response = await editInscripcion(token!, editData.id, data);
      if (!response.ok) {
        handleCloseEditInscripcionDialog();
        showAlert('Error', response.errorMessage.error, 'error');
        throw new Error('Error al editar la inscripción');
      }
      handleCloseEditInscripcionDialog();
      showAlert('¡Éxito!', 'Inscripción actualizada exitosamente', 'success');
      refetchData(); // Refetch data after saving
    } catch (error: any) {
      showAlert('Error', error.message, 'error');
    }
  });

  const handleSaveEditGrupo = handleSubmitEdit(async (data) => {
    if (!data) return;
    const grupoId = data.grupoId;
    const estudianteId = editData?.estudianteId;

    try {
      const response = await editGrupo(token!, editData.id, grupoId, estudianteId);
      if (!response.ok) {
        handleCloseEditGrupoDialog();
        showAlert('Error', response.errorMessage.error, 'error');
        throw new Error('Error al editar el grupo');
      }
      showAlert('¡Éxito!', 'Grupo editado exitosamente', 'success');
      handleCloseEditGrupoDialog();
      refetchData(); // Refetch data after saving
    } catch (error: any) {
      handleCloseEditGrupoDialog();
      showAlert('Error', error.message, 'error');
    }
  });

  const handleReenviarPago = async (selectedInscripcion: any) => {
    try {
      const response = await reenviarPago(token!, selectedInscripcion.id);
      if (!response.ok) {
        showAlert('Error', response.errorMessage.error, 'error');

        throw new Error('Error al reenviar el correo de pago');
      }
      showAlert('¡Éxito!', 'Correo de pago reenviado exitosamente', 'success');
    } catch (error: any) {
      showAlert('Error', error.message, 'error');

    }
  };

  const handleConfirmarPago = async (selectedInscripcion: any) => {
    try {
      const response = await confirmarPago(token!, selectedInscripcion.id);
      if (!response.ok) {
        showAlert('Error', response.errorMessage.error, 'error');

        throw new Error('Error al enviar correo de confirmacion de pago');
      }
      showAlert('¡Éxito!', 'Correo de confirmacion enviado exitosamente', 'success');
    } catch (error: any) {
      showAlert('Error', error.message, 'error');

    }
  };

  return (
    <AdminLayout title='Estudiantes por grupo'>
      {loading ? <LoadingScreen /> :
        error ? <div>Error: {error}</div>
          : <Paper
            sx={{ overflow: "hidden", width: "100%", borderRadius: 2, mt: 0, p: 2 }}
          >
            <Grid item xs={12} container alignItems="center" justifyContent="space-between" mb={1}>
              <Box >
                <Typography
                  fontSize={22} component="div" align="left" >
                  Todos los Estudiantes
                </Typography>
              </Box>
              <Grid item xs={12} sm={5} mr={4}>
                <FormControl fullWidth>
                  {cursos && <Select
                    variant="outlined"
                    value={selectedCursoId}
                    onChange={handleCursoChange}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                  >
                    <MenuItem value="">
                      <em>Seleccionar Curso</em>
                    </MenuItem>
                    {cursos.map((curso: any) => (
                      <MenuItem key={curso.id} value={curso.id}>
                        {curso?.nombre}
                      </MenuItem>
                    ))}
                  </Select>}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <Select
                    variant="filled"
                    value={selectedGroupId || id}
                    onChange={handleGrupoChange}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                  >
                    <MenuItem value="allGroups">
                      <em>Mostrar todos los grupos</em>
                    </MenuItem>
                    {grupos.map((grupo: any) => (
                      <MenuItem key={grupo.id} value={grupo.id}>
                        {grupo?.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box sx={{ height: '85vh' }}>
              <DataGrid
                columns={columns}
                rows={rows}
                slots={{ toolbar: GridToolbar }}
                filterModel={filterModel}
                onFilterModelChange={(newModel) => setFilterModel(newModel)}
                slotProps={{
                  toolbar: { showQuickFilter: true, },
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                  setColumnVisibilityModel(newModel)
                }
              />
            </Box>
          </Paper>
      }
      {/* Dialog for Editing Inscripcion */}
      <Dialog
        open={openEditInscripcionDialog}
        onClose={handleCloseEditInscripcionDialog}
        fullWidth
        maxWidth="md"
      >
        <form onSubmit={handleSaveEditInscripcion}>
          <DialogTitle sx={{
            fontWeight: "bold",
            fontSize: "2rem",
            color: "secondary.dark",

          }}>
            Editar Inscripción
          </DialogTitle>

          <DialogContent>

            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <Typography
                  sx={{

                    marginBottom: 2,
                    borderBottom: "2px solid #ccc",
                    paddingBottom: 1,
                    color: "primary.light",
                  }}
                  variant="h6" gutterBottom>
                  Estado de la Inscripción
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          id="pagoRealizado"
                          {...registerEdit("pagoRealizado")}
                          checked={watch("pagoRealizado")}
                          onChange={(e) => setValueEdit("pagoRealizado", e.target.checked)}
                        />
                      }
                      label="Pago Realizado"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          id="libroEntregado"
                          {...registerEdit("libroEntregado")}
                          checked={watch("libroEntregado")}
                          onChange={(e) => setValueEdit("libroEntregado", e.target.checked)}
                        />
                      }
                      label="Libro Entregado"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          id="listaDeEspera"
                          {...registerEdit("listaDeEspera")}
                          checked={watch("listaDeEspera")}
                          onChange={(e) => setValueEdit("listaDeEspera", e.target.checked)}
                        />
                      }
                      label="En Lista de Espera"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth error={Boolean(errorsEdit.estadoInscripcion)} variant='outlined' margin="dense">
                      <InputLabel id="estado-label">Estado de Inscripción</InputLabel>
                      <Select
                        labelId="estado-label"
                        id="estado-select"
                        label="Estado de Inscripción"
                        {...registerEdit("estadoInscripcion")}
                        value={watch("estadoInscripcion")}
                        onChange={(e) => {
                          setValueEdit("estadoInscripcion", e.target.value, { shouldValidate: true });
                        }}
                      >
                        <MenuItem value="En-espera">En Espera</MenuItem>
                        <MenuItem value="Pre-inscrito">Pre-inscrito</MenuItem>
                        <MenuItem value="Inscrito">Inscrito</MenuItem>
                      </Select>
                      <FormHelperText>{errorsEdit.estadoInscripcion?.message}</FormHelperText>
                    </FormControl>

                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={7}>
                <Typography
                  sx={{

                    marginBottom: 2,
                    borderBottom: "2px solid #ccc",
                    paddingBottom: 1,
                    color: "primary.light",
                  }} variant="h6" gutterBottom>
                  Encargado de Retiro
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      margin="dense"
                      id="encargadoRetiroNombre"
                      label="Nombre del Encargado de Retiro"
                      fullWidth
                      variant="outlined"
                      {...registerEdit("encargadoRetiroNombre")}
                      error={Boolean(errorsEdit.encargadoRetiroNombre)}
                      helperText={errorsEdit.encargadoRetiroNombre?.message}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      margin="dense"
                      id="encargadoRetiroTelefono"
                      label="Teléfono del Encargado de Retiro"
                      fullWidth
                      variant="outlined"
                      {...registerEdit("encargadoRetiroTelefono")}
                      error={Boolean(errorsEdit.encargadoRetiroTelefono)}
                      helperText={errorsEdit.encargadoRetiroTelefono?.message}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={12}>
                <Typography sx={{

                  marginBottom: 2,
                  borderBottom: "2px solid #ccc",
                  paddingBottom: 1,
                  color: "primary.light",
                }} variant="h6" gutterBottom>
                  Sede de Correos
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={5}>
                    <Autocomplete
                      disableClearable
                      options={sedesCorreosCR}
                      getOptionLabel={(sede: any) => `${sede.nombre}`}
                      value={sedesCorreosCR.find((sede: any) => sede.id === Number(watch("sedeCorreosCRId"))) || null}
                      onChange={(event, sede) => {
                        setValueEdit("sedeCorreosCRId", sede ? sede.id.toString() : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params as any}
                          variant="outlined"
                          label="Buscar Sede de correos de Costa Rica"
                          error={!!errorsEdit.sedeCorreosCRId}
                          helperText={errorsEdit.sedeCorreosCRId?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField


                      id="provincia"
                      label="Provincia"
                      fullWidth
                      variant="filled"
                      value={
                        sedesCorreosCR.find((sede: any) => sede.id === Number(watch("sedeCorreosCRId")))?.provincia || ""
                      }
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditInscripcionDialog} color="error">Cancelar</Button>
            <Button type="submit" color="primary">{loading ? <CircularProgress /> : "Guardar"}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog for Editing Grupo */}
      <Dialog
        open={openEditGrupoDialog}
        onClose={handleCloseEditGrupoDialog}
        fullWidth
        maxWidth="xs"
      >
        <form onSubmit={handleSaveEditGrupo}>
          <DialogTitle
            sx={{
              fontWeight: "bold",
              fontSize: "2rem",
              color: "secondary.dark",

            }}>
            Editar Grupo
          </DialogTitle>
          <DialogContent>

            <TextField
              margin="dense"
              id="nombreEstudiante"
              label="Nombre del Estudiante que estamos cambiando de grupo"
              fullWidth
              variant="filled"

              value={editData?.nombreEstudiante}
              disabled
              sx={{ marginBottom: 2 }}
            />

            <FormControl fullWidth margin="dense">
              <InputLabel id="grupo-label">Grupo</InputLabel>
              <Select
                labelId="grupo-label"
                id="grupo-select"
                label="Grupo"
                {...registerEdit("grupoId")}
                value={watch("grupoId")}
                onChange={(e) => {
                  setValueEdit("grupoId", e.target.value, { shouldValidate: true });
                }}
                error={Boolean(errorsEdit.grupoId)}
              >
                {grupos.map((grupo: any) => (
                  <MenuItem key={grupo.id} value={grupo.id}>
                    {grupo.nombre}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errorsEdit.grupoId?.message}</FormHelperText>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditGrupoDialog} color="error">Cancelar</Button>
            <Button type="submit" color="primary">{loading ? <CircularProgress /> : "Guardar"}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog for Editing Factura */}
      <Dialog
        open={openEditFacturaDialog}
        onClose={handleCloseEditFacturaDialog}
        fullWidth
        maxWidth="md"
      >
        <form onSubmit={handleSaveEditFactura}>
          <DialogTitle sx={{
            fontWeight: "bold",
            fontSize: "2rem",
            color: "secondary.dark",
          }}>
            Editar Factura
          </DialogTitle>

          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <Typography
                  sx={{
                    marginBottom: 2,
                    borderBottom: "2px solid #ccc",
                    paddingBottom: 1,
                    color: "primary.light",
                  }}
                  variant="h6" gutterBottom>
                  Factura Electrónica
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={Boolean(errorsEdit.facturaElectronica?.tipoCedula)} variant='outlined' margin="dense">
                      <InputLabel id="tipoCedula-label">Tipo de Cédula</InputLabel>
                      <Select
                        labelId="tipoCedula-label"
                        id="tipoCedula-select"
                        label="Tipo de Cédula"
                        {...registerEdit("facturaElectronica.tipoCedula")}
                        value={watch("facturaElectronica.tipoCedula")}
                      >
                        <MenuItem value="Fisica">Persona Física</MenuItem>
                        <MenuItem value="Juridica">Persona Jurídica</MenuItem>
                        <MenuItem value="DIMEX">DIMEX</MenuItem>
                        <MenuItem value="NITE">NITE</MenuItem>
                        <MenuItem value="Extranjero">Extranjero(Fuera CR)</MenuItem>
                      </Select>
                      <FormHelperText>{errorsEdit.facturaElectronica?.tipoCedula?.message}</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      margin="dense"
                      id="cedula"
                      label="Cédula"
                      fullWidth
                      variant="outlined"
                      {...registerEdit("facturaElectronica.cedula")}
                      error={Boolean(errorsEdit.facturaElectronica?.cedula)}
                      helperText={errorsEdit.facturaElectronica?.cedula?.message}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      margin="dense"
                      id="correo"
                      label="Correo"
                      fullWidth
                      variant="outlined"
                      {...registerEdit("facturaElectronica.correo")}
                      error={Boolean(errorsEdit.facturaElectronica?.correo)}
                      helperText={errorsEdit.facturaElectronica?.correo?.message}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      margin="dense"
                      id="telefono"
                      label="Teléfono"
                      fullWidth
                      variant="outlined"
                      {...registerEdit("facturaElectronica.telefono")}
                      error={Boolean(errorsEdit.facturaElectronica?.telefono)}
                      helperText={errorsEdit.facturaElectronica?.telefono?.message}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          id="facturaRequerida"
                          {...registerEdit("facturaRequerida")}
                          checked={watch("facturaRequerida")}
                          onChange={(e) => setValueEdit("facturaRequerida", e.target.checked)}
                        />
                      }
                      label="Factura Requerida"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditFacturaDialog} color="error">Cancelar</Button>
            <Button type="submit" color="primary">{loading ? <CircularProgress /> : "Guardar"}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </AdminLayout>
  );
};